import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface FilterBarProps {
  categories: { id: string; name: string; count: number }[];
  genres: string[];
  eventTypes: string[];
}

export default function ArtistFilterBar({ categories, genres, eventTypes }: FilterBarProps) {
  const { t } = useTranslation('artists');
  const [searchParams, setSearchParams] = useSearchParams();

  const activeCategory = searchParams.get('category') || 'all';
  const activeGenre = searchParams.get('genre') || 'all';
  const activeEvent = searchParams.get('event') || 'all';
  const activeSort = searchParams.get('sort') || 'featured';

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === 'all') {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const hasActiveFilters = activeCategory !== 'all' || activeGenre !== 'all' || activeEvent !== 'all';

  const clearAll = () => {
    const params = new URLSearchParams();
    const q = searchParams.get('q');
    if (q) params.set('q', q);
    setSearchParams(params);
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* Category Filter */}
        <div className="relative group">
          <select
            value={activeCategory}
            onChange={(e) => updateFilter('category', e.target.value)}
            className="appearance-none px-4 py-2.5 pr-10 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-600/20 focus:border-amber-600 cursor-pointer hover:border-gray-300 transition-all"
          >
            <option value="all">{t('filters.category')}: {t('filters.all')}</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name} ({cat.count})
              </option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none text-gray-400">
            <i className="ri-arrow-down-s-line text-sm"></i>
          </div>
        </div>

        {/* Genre Filter */}
        <div className="relative group">
          <select
            value={activeGenre}
            onChange={(e) => updateFilter('genre', e.target.value)}
            className="appearance-none px-4 py-2.5 pr-10 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-600/20 focus:border-amber-600 cursor-pointer hover:border-gray-300 transition-all"
          >
            <option value="all">{t('filters.genre')}: {t('filters.all')}</option>
            {genres.map((genre) => (
              <option key={genre} value={genre}>{genre}</option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none text-gray-400">
            <i className="ri-arrow-down-s-line text-sm"></i>
          </div>
        </div>

        {/* Event Type Filter */}
        <div className="relative group">
          <select
            value={activeEvent}
            onChange={(e) => updateFilter('event', e.target.value)}
            className="appearance-none px-4 py-2.5 pr-10 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-600/20 focus:border-amber-600 cursor-pointer hover:border-gray-300 transition-all"
          >
            <option value="all">{t('filters.eventType')}: {t('filters.all')}</option>
            {eventTypes.map((et) => (
              <option key={et} value={et}>{et}</option>
            ))}
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none text-gray-400">
            <i className="ri-arrow-down-s-line text-sm"></i>
          </div>
        </div>

        {/* Sort */}
        <div className="relative group ml-auto">
          <select
            value={activeSort}
            onChange={(e) => updateFilter('sort', e.target.value)}
            className="appearance-none px-4 py-2.5 pr-10 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-600/20 focus:border-amber-600 cursor-pointer hover:border-gray-300 transition-all"
          >
            <option value="featured">{t('sort.featured')}</option>
            <option value="name">{t('sort.name')}</option>
            <option value="category">{t('sort.category')}</option>
          </select>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none text-gray-400">
            <i className="ri-arrow-down-s-line text-sm"></i>
          </div>
        </div>
      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          {activeCategory !== 'all' && (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
              {categories.find(c => c.id === activeCategory)?.name || activeCategory}
              <button
                onClick={() => updateFilter('category', 'all')}
                className="w-4 h-4 flex items-center justify-center hover:text-amber-900"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </span>
          )}
          {activeGenre !== 'all' && (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
              {activeGenre}
              <button
                onClick={() => updateFilter('genre', 'all')}
                className="w-4 h-4 flex items-center justify-center hover:text-amber-900"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </span>
          )}
          {activeEvent !== 'all' && (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
              {activeEvent}
              <button
                onClick={() => updateFilter('event', 'all')}
                className="w-4 h-4 flex items-center justify-center hover:text-amber-900"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </span>
          )}
          <button
            onClick={clearAll}
            className="text-xs text-gray-500 hover:text-amber-700 underline underline-offset-2 transition-colors"
          >
            {t('clearFilters')}
          </button>
        </div>
      )}
    </div>
  );
}